/**
 * One clue, one screen at a time: the question, the answer, what happens when
 * they get it right, and then the reward — the clue as the recipient will see it.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../chassis/Icon";
import CluePreview from "../preview/CluePreview";
import { BigField, Chip, ChoiceRow, ClueMeter, GhostPill, Heading, PrimaryButton, ProBadge, SecondaryButton, StepChip, Sub, ValuePill } from "../chassis/widgets";
import { C, SERIF, TINT } from "../chassis/tokens";
import { CELEBRATIONS, PRO_CELEBRATIONS, LIMITS, MIN_CLUES, RECOMMENDED_CLUES, MAX_CLUES } from "../constants";
import { completeClues, currentClue } from "../state";
import { safeMediaUrl } from "../../../utils/safeMedia";
import { themedText } from "../themedCopy";

const IDEA_KEYS = ["met", "song", "nickname", "trip", "joke"];

export function clueChip(state, tr, done = false) {
  const n = state.clueIndex + 1;
  return (
    <StepChip
      badge={done ? "check" : n}
      tone={done ? "ok" : "accent"}
      label={done ? tr("app.creator.clue.ready", { n }) : tr("app.creator.clue.number", { n })}
    />
  );
}

export function QuestionScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr)}
      <Heading accent={state.recipientName ? `${state.recipientName}?` : null}>
        {state.recipientName ? tr("app.creator.question.headingNamed") : tr("app.creator.question.heading")}
      </Heading>
      <Sub>{tr("app.creator.question.sub")}</Sub>

      <BigField
        id="creator-question"
        label={tr("app.creator.question.label")}
        value={clue.question || ""}
        onChange={(v) => dispatch({ type: "setQuestion", value: v })}
        placeholder={themedText(tr, state.theme, "question.placeholder")}
        multiline
        rows={2}
        fontSize={24}
      />

      <div style={{ marginTop: 26, display: "flex", alignItems: "center", gap: 7 }}>
        <Icon name="bulb" size={15} color={C.accent} strokeWidth={2.2} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.body }}>{tr("app.creator.question.ideasLabel")}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 11, paddingBottom: 8 }}>
        {IDEA_KEYS.map((key) => {
          const text = themedText(tr, state.theme, `question.ideas.${key}`);
          return (
            <Chip key={key} selected={clue.question === text} onClick={() => dispatch({ type: "setQuestion", value: text })}>
              {text}
            </Chip>
          );
        })}
      </div>
    </>
  );
}

export function AnswerScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr)}
      <div style={{ marginTop: 18, display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px", borderRadius: 18, background: TINT.glass(0.65), border: `1px solid ${C.line}` }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#E7A3B7" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden="true">
          <path d="M4 10.5C4 7 6 5 9 4.5l.6 1.8C8 7 7.3 8.2 7.3 9.5H10V16H4zm10 0C14 7 16 5 19 4.5l.6 1.8C18 7 17.3 8.2 17.3 9.5H20V16h-6z" />
        </svg>
        <span style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 19, lineHeight: 1.3, color: C.muted }}>{clue.question}</span>
      </div>

      <Heading accent={tr("app.creator.answer.headingAccent")}>{tr("app.creator.answer.heading")}</Heading>
      <Sub>{tr("app.creator.answer.sub")}</Sub>

      <BigField
        id="creator-answer"
        label={tr("app.creator.answer.label")}
        value={clue.answer || ""}
        onChange={(v) => dispatch({ type: "setAnswer", value: v })}
        placeholder={tr("app.creator.answer.placeholder")}
        fontSize={30}
      />
      <div style={{ height: 16 }} />
    </>
  );
}

const CELEBRATION_ART = {
  default: (
    <span style={{ position: "relative", flexShrink: 0, width: 46, height: 46, borderRadius: 14, background: "linear-gradient(150deg, #FF8AB0 0%, #D8406E 100%)", overflow: "hidden", display: "grid", placeItems: "center" }}>
      <span style={{ position: "absolute", top: 7, left: 8, width: 5, height: 5, borderRadius: 1, background: "#FFE55C", transform: "rotate(25deg)" }} />
      <span style={{ position: "absolute", top: 10, right: 8, width: 4, height: 4, borderRadius: 999, background: "#FFFFFF" }} />
      <span style={{ position: "absolute", bottom: 8, left: 10, width: 4, height: 4, borderRadius: 999, background: "#9FE3F5" }} />
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" /></svg>
    </span>
  ),
  message: (
    <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, background: C.accentSoft, display: "grid", placeItems: "center" }}>
      <Icon name="message" size={22} color={C.accent} strokeWidth={2} />
    </span>
  ),
  photo: (
    <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, background: "#F6EFE6", display: "grid", placeItems: "center" }}>
      <span style={{ width: 26, height: 30, boxSizing: "border-box", borderRadius: 3, background: "#FFFFFF", padding: "3px 3px 8px", boxShadow: `0 3px 6px -2px ${TINT.shadow(0.3)}`, transform: "rotate(-7deg)", display: "block" }}>
        <span style={{ display: "block", width: "100%", height: "100%", borderRadius: 1, background: "linear-gradient(160deg, #FFC9A8 0%, #E8708E 100%)" }} />
      </span>
    </span>
  ),
  video: (
    <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, background: "#2A1420", display: "grid", placeItems: "center" }}>
      <span style={{ width: 24, height: 24, borderRadius: 999, background: "#FFFFFF", display: "grid", placeItems: "center" }}>
        <Icon name="play" size={10} color="#2A1420" fill="#2A1420" />
      </span>
    </span>
  ),
  song: (
    <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, background: "linear-gradient(150deg, #EFE3FB 0%, #D9C4F2 100%)", display: "grid", placeItems: "center" }}>
      <Icon name="music" size={22} color="#6B3FA0" strokeWidth={2.1} />
    </span>
  ),
};

export function CelebrationScreen({ state, dispatch, isPro, onMediaLocked }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr)}
      <Heading size={30} accent={tr("app.creator.celebration.headingAccent")}>{tr("app.creator.celebration.heading")}</Heading>
      <Sub>{tr("app.creator.celebration.sub")}</Sub>

      <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 9, paddingBottom: 8 }}>
        {CELEBRATIONS.map((type) => {
          const pro = PRO_CELEBRATIONS.includes(type);
          return (
            <ChoiceRow
              key={type}
              id={`creator-celebration-${type}`}
              name="creator-celebration"
              checked={(clue.celebrationType || "default") === type}
              onChange={() => (pro && !isPro ? onMediaLocked() : dispatch({ type: "setCelebration", value: type }))}
              art={CELEBRATION_ART[type]}
              title={tr(`app.creator.celebration.options.${type}.title`)}
              description={tr(`app.creator.celebration.options.${type}.description`)}
              badge={pro && !isPro ? <ProBadge /> : null}
            />
          );
        })}
      </div>
    </>
  );
}

/** Message text, a photo or song upload, or a video link — one per celebration type. */
export function CelebrationDetailScreen({ state, dispatch, onUploadMedia }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  const type = clue.celebrationType;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function pick(file) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const url = await onUploadMedia(file, type === "song" ? "song" : "photo");
      dispatch({ type: "setCelebrationContent", value: url });
    } catch (err) {
      setError(tr(`app.creator.media.errors.${(err && err.code) || "upload-failed"}`, { defaultValue: tr("app.creator.media.errors.upload-failed") }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {clueChip(state, tr)}
      <Heading size={30}>{tr(`app.creator.celebrationDetail.${type}.heading`)}</Heading>
      <Sub>{tr(`app.creator.celebrationDetail.${type}.sub`)}</Sub>

      {type === "message" && (
        <BigField
          id="creator-celebration-message"
          label={tr("app.creator.celebrationDetail.message.label")}
          value={clue.celebrationContent || ""}
          onChange={(v) => dispatch({ type: "setCelebrationContent", value: v })}
          placeholder={tr("app.creator.celebrationDetail.message.placeholder")}
          multiline
          rows={3}
          fontSize={20}
          count={`${(clue.celebrationContent || "").length} / ${LIMITS.message}`}
        />
      )}

      {type === "video" && (
        <BigField
          id="creator-celebration-video"
          label={tr("app.creator.celebrationDetail.video.label")}
          value={clue.celebrationContent || ""}
          onChange={(v) => dispatch({ type: "setCelebrationContent", value: v })}
          placeholder={tr("app.creator.celebrationDetail.video.placeholder")}
          fontSize={16}
          inputMode="url"
        />
      )}

      {(type === "photo" || type === "song") && (
        <div style={{ marginTop: 24 }}>
          <input
            ref={fileRef}
            type="file"
            accept={type === "song" ? "audio/*" : "image/*"}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              e.target.value = "";
              pick(file);
            }}
          />
          <button
            type="button"
            data-testid="creator-media-pick"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={busy}
            style={{
              width: "100%", padding: "26px 18px", borderRadius: 24, cursor: busy ? "wait" : "pointer",
              border: `2px dashed ${clue.celebrationContent ? C.accent : C.dash}`,
              background: clue.celebrationContent ? C.accentSoft : TINT.glass(0.6),
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            }}
          >
            <Icon name={type === "song" ? "music" : "image"} size={26} color={C.accent} strokeWidth={2} />
            <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>
              {busy
                ? tr("app.creator.media.uploading")
                : clue.celebrationContent
                ? tr(`app.creator.media.replace.${type}`)
                : tr(`app.creator.media.pick.${type}`)}
            </span>
            <span style={{ fontSize: 12, color: C.faint }}>{tr(`app.creator.media.hint.${type}`)}</span>
          </button>

          {type === "photo" && safeMediaUrl(clue.celebrationContent, "image") && (
            <img src={safeMediaUrl(clue.celebrationContent, "image")} alt="" style={{ display: "block", width: "100%", marginTop: 14, borderRadius: 18, maxHeight: 220, objectFit: "cover" }} />
          )}
          {type === "song" && safeMediaUrl(clue.celebrationContent, "audio") && (
            <audio controls src={safeMediaUrl(clue.celebrationContent, "audio")} style={{ width: "100%", marginTop: 14 }} />
          )}
        </div>
      )}

      {error ? (
        <p role="alert" style={{ margin: "14px 2px 0", fontSize: 13, fontWeight: 600, color: C.accentDeep }}>{error}</p>
      ) : null}
      <div style={{ height: 16 }} />
    </>
  );
}

export function HintScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr)}
      <Heading size={30} accent={state.recipientName || null}>{tr("app.creator.hint.heading")}</Heading>
      <Sub>{tr("app.creator.hint.sub")}</Sub>
      <BigField
        id="creator-hint"
        label={tr("app.creator.hint.label")}
        value={clue.hint || ""}
        onChange={(v) => dispatch({ type: "setHint", value: v })}
        placeholder={themedText(tr, state.theme, "hint.placeholder")}
        fontSize={20}
      />
    </>
  );
}

export function ClueNameScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr)}
      <Heading size={30}>{tr("app.creator.clueName.heading")}</Heading>
      <Sub>{tr("app.creator.clueName.sub")}</Sub>
      <BigField
        id="creator-clue-name"
        label={tr("app.creator.clueName.label")}
        value={clue.title || ""}
        onChange={(v) => dispatch({ type: "setClueTitle", value: v })}
        placeholder={themedText(tr, state.theme, "clueName.placeholder")}
        fontSize={22}
      />
    </>
  );
}

/** Meter copy changes with how many clues are written (decision D2). */
export function meterProps(written, tr) {
  if (written < MIN_CLUES) {
    return {
      written,
      headline: tr("app.creator.meter.written", { count: written }),
      note: tr("app.creator.meter.toGo", { count: MIN_CLUES - written }),
    };
  }
  if (written >= MAX_CLUES) {
    return {
      written,
      headline: tr("app.creator.meter.written", { count: written }),
      note: tr("app.creator.meter.max"),
      hint: tr("app.creator.meter.maxHint"),
    };
  }
  return {
    written,
    headline: tr("app.creator.meter.written", { count: written }),
    note: written < RECOMMENDED_CLUES ? tr("app.creator.meter.canSend") : tr("app.creator.meter.room", { count: MAX_CLUES - written }),
    hint: written < RECOMMENDED_CLUES ? tr("app.creator.meter.recommend") : undefined,
  };
}

export function RewardScreen({ state, dispatch, LevelPlay, themeVars, previewConfig, previewIndex }) {
  const { t: tr } = useTranslation();
  const written = completeClues(state).length;
  const clue = currentClue(state) || {};
  return (
    <>
      {clueChip(state, tr, true)}
      <Heading size={28} accent={state.recipientName || null} tail={state.recipientName ? tr("app.creator.reward.headingTail") : null}>
        {state.recipientName ? tr("app.creator.reward.heading") : tr("app.creator.reward.headingPlain")}
      </Heading>

      <CluePreview
        LevelPlay={LevelPlay}
        themeVars={themeVars}
        config={previewConfig}
        index={previewIndex}
        height={330}
      />

      <ClueMeter {...meterProps(written, tr)} />

      {/* Once a hint or a name is written it shows here, so the user sees what
          they set without opening the screen again. */}
      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16, flexWrap: "wrap", paddingBottom: 8 }}>
        {clue.hint && clue.hint.trim() ? (
          <ValuePill testId="creator-add-hint" icon="bulb" label={tr("app.creator.reward.hintSet")} value={clue.hint} onClick={() => dispatch({ type: "next", intent: "hint" })} />
        ) : (
          <GhostPill testId="creator-add-hint" onClick={() => dispatch({ type: "next", intent: "hint" })}>
            {tr("app.creator.reward.addHint")}
          </GhostPill>
        )}
        {clue.title && clue.title.trim() ? (
          <ValuePill testId="creator-name-clue" label={tr("app.creator.reward.nameSet")} value={clue.title} onClick={() => dispatch({ type: "next", intent: "name" })} />
        ) : (
          <GhostPill testId="creator-name-clue" onClick={() => dispatch({ type: "next", intent: "name" })}>
            {tr("app.creator.reward.nameClue")}
          </GhostPill>
        )}
      </div>
    </>
  );
}

/**
 * Below the minimum there is one way on, so "That's all my clues" is not
 * rendered at all — no greyed-out button to puzzle over. At the recommended
 * count the add button nudges toward a third clue; at the maximum it goes away.
 */
export function rewardFooter({ state, tr, dispatch }) {
  const written = completeClues(state).length;

  if (written < MIN_CLUES) {
    return (
      <PrimaryButton testId="creator-next" onClick={() => dispatch({ type: "next", intent: "add" })}>
        {tr("app.creator.reward.writeNext", { n: written + 1 })}
      </PrimaryButton>
    );
  }

  return (
    <>
      {written < MAX_CLUES && (
        <SecondaryButton testId="creator-add-clue" icon="plus" onClick={() => dispatch({ type: "next", intent: "add" })}>
          {written < RECOMMENDED_CLUES ? tr("app.creator.reward.addThird") : tr("app.creator.reward.addAnother")}
        </SecondaryButton>
      )}
      <PrimaryButton testId="creator-done-clues" onClick={() => dispatch({ type: "next", intent: "done" })} icon={null}>
        {tr("app.creator.reward.done")}
      </PrimaryButton>
    </>
  );
}
